import { useEffect, useState } from 'react'
import { getTeamMembers } from '../../lib/api'
import GlassCard from '../ui/GlassCard'
import SectionTitle from '../ui/SectionTitle'
import ScrollReveal from '../ui/ScrollReveal'
import LoadingSpinner from '../ui/LoadingSpinner'
import Avatar3D from '../ui/Avatar3D'
import Parallax from '../ui/Parallax'
import type { TeamMember } from '../../types'

export default function TeamSection() {
  const [data, setData] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getTeamMembers().then((res) => {
      setData(res)
      setLoading(false)
    })
  }, [])

  if (loading) return <LoadingSpinner text="Loading team..." />

  return (
    <section className="relative py-24">
      <Parallax speed={0.15} className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px]">
        <div className="w-full h-full rounded-full bg-premium-600/5 blur-[120px]" />
      </Parallax>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          label="Our Team"
          title="Meet the Experts"
          subtitle="A diverse team of passionate creators, engineers, and strategists dedicated to your success."
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {data.map((member, i) => (
            <ScrollReveal key={member.id} delay={i * 0.1}>
              <GlassCard className="p-6 text-center group h-full flex flex-col">
                <div className="relative mx-auto mb-5 w-24 h-24 shrink-0">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-electric-blue to-electric-purple blur-md opacity-50 group-hover:opacity-80 transition-opacity duration-500" />
                  <div className="relative w-24 h-24 rounded-full ring-2 ring-white/10 overflow-hidden bg-gradient-to-br from-electric-blue/5 to-electric-purple/5">
                    <Avatar3D name={member.name} />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white">{member.name}</h3>
                <p className="text-sm text-electric-blue font-medium mt-1">
                  {member.role}
                </p>
                <p className="mt-3 text-sm text-gray-400 leading-relaxed flex-1">
                  {member.bio}
                </p>
                <div className="flex justify-center gap-2 mt-5 shrink-0">
                  {member.socials.linkedin && (
                    <a
                      href={member.socials.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${member.name} LinkedIn`}
                      className="team-social w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 transition-all duration-300 hover:-translate-y-1 hover:scale-110"
                      style={{ '--hover-bg': '#0077b5', '--glow': 'rgba(0, 119, 181, 0.5)' } as React.CSSProperties}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </a>
                  )}
                  {member.socials.twitter && (
                    <a
                      href={member.socials.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${member.name} Twitter`}
                      className="team-social w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 transition-all duration-300 hover:-translate-y-1 hover:scale-110"
                      style={{ '--hover-bg': '#1da1f2', '--glow': 'rgba(29, 161, 242, 0.5)' } as React.CSSProperties}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </a>
                  )}
                  {member.socials.github && (
                    <a
                      href={member.socials.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${member.name} GitHub`}
                      className="team-social w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 transition-all duration-300 hover:-translate-y-1 hover:scale-110"
                      style={{ '--hover-bg': 'rgba(255,255,255,0.15)', '--glow': 'rgba(255,255,255,0.2)' } as React.CSSProperties}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                        <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
                      </svg>
                    </a>
                  )}
                  <style>{`
                    .team-social:hover {
                      background: var(--hover-bg) !important;
                      border-color: transparent !important;
                      box-shadow: 0 0 15px var(--glow);
                    }
                    .team-social:hover svg {
                      animation: teamShake 0.5s;
                    }
                    @keyframes teamShake {
                      0%, 100% { transform: translateX(0); }
                      20% { transform: translateX(-2px); }
                      40% { transform: translateX(2px); }
                      60% { transform: translateX(-2px); }
                      80% { transform: translateX(2px); }
                    }
                  `}</style>
                </div>
              </GlassCard>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  )
}
